import React, { useState, useEffect } from 'react';
import { videoRecorderService } from '../../services/videoRecorderService';
import { VideoRecording, VideoShare } from '../../types/videoRecorder';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

interface VideoShareComponentProps {
  videoId: string;
  onClose?: () => void;
}

const VideoShareComponent: React.FC<VideoShareComponentProps> = ({ videoId, onClose }) => {
  const { user } = useAuth();
  const [video, setVideo] = useState<VideoRecording | null>(null);
  const [shares, setShares] = useState<VideoShare[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [accessType, setAccessType] = useState<'view' | 'edit'>('view');
  const [expiryDays, setExpiryDays] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publicLink, setPublicLink] = useState<string | null>(null);
  // Signed URL for direct file access (temporary)
  const [expiresInMinutes, setExpiresInMinutes] = useState<number | ''>('');
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);

  const sanitizeFilename = (name: string) => {
    const base = name.replace(/[^a-zA-Z0-9-_\. ]/g, '').trim() || 'video';
    // Collapse spaces and dots, avoid trailing dots
    return base.replace(/\s+/g, ' ').replace(/\.+$/,'');
  };

  // Load video and existing shares
  useEffect(() => {
    const fetchVideoAndShares = async () => {
      setIsLoading(true);
      try {
        // Get video details
        const videoData = await videoRecorderService.getVideoById(videoId);
        setVideo(videoData);
        
        // Get existing shares
        if (videoData) {
          const sharesData = await videoRecorderService.getVideoShares(videoId);
          setShares(sharesData);
          
          // If the video is public, generate a public link
          if (videoData.is_public) {
            const baseUrl = process.env.NODE_ENV === 'production' 
              ? 'https://explanatum.co.uk' 
              : window.location.origin;
            setPublicLink(`${baseUrl}/videos/watch/${videoId}`);
          }
        }
      } catch (error) {
        console.error('Error fetching video and shares:', error);
        toast.error('Failed to load sharing information');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVideoAndShares();
  }, [videoId]);
  
  const handleCreateShare = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Calculate expiry date if set
      const expiresAt = expiryDays ? new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000) : null;
      
      // Create share
      const share = await videoRecorderService.shareVideo(
        videoId,
        email.trim(),
        accessType,
        expiresAt
      );
      
      if (share) {
        setShares(prev => [...prev, share]);
        setEmail('');
        toast.success('Share created successfully');
      } else {
        throw new Error('Failed to create share');
      }
    } catch (error) {
      console.error('Error creating share:', error);
      toast.error('Failed to create share');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleTogglePublic = async () => {
    if (!video || isTogglingPublic) return;
    setIsTogglingPublic(true);
    const prev = video;
    const nextPublic = !prev.is_public;
    // Optimistic UI
    setVideo({ ...prev, is_public: nextPublic });
    if (nextPublic) {
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://explanatum.co.uk' 
        : window.location.origin;
      setPublicLink(`${baseUrl}/videos/watch/${videoId}`);
    } else {
      setPublicLink(null);
    }
    try {
      const updatedVideo = await videoRecorderService.updateVideo(
        videoId,
        { is_public: nextPublic }
      );
      if (!updatedVideo) {
        throw new Error('No update returned');
      }
      setVideo(updatedVideo);
      toast.success(updatedVideo.is_public ? 'Video is now public' : 'Video is now private');
    } catch (error) {
      console.error('Error toggling public status:', error);
      // Rollback
      setVideo(prev);
      if (prev.is_public) {
        const baseUrl = process.env.NODE_ENV === 'production' 
          ? 'https://explanatum.co.uk' 
          : window.location.origin;
        setPublicLink(`${baseUrl}/videos/watch/${videoId}`);
      } else {
        setPublicLink(null);
      }
      toast.error('Failed to update video visibility');
    } finally {
      setIsTogglingPublic(false);
    }
  };
  
  const handleRemoveShare = async (shareId: string) => {
    try {
      const success = await videoRecorderService.deleteShare(shareId);
      
      if (success) {
        setShares(prev => prev.filter(share => share.id !== shareId));
        toast.success('Share removed successfully');
      } else {
        throw new Error('Failed to remove share');
      }
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Failed to remove share');
    }
  };
  
  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(
      () => {
        toast.success('Link copied to clipboard');
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast.error('Failed to copy link');
      }
    );
  };

  const handleGenerateSignedLink = async () => {
    if (!video) return;
    try {
      setIsGeneratingLink(true);
      const mins = expiresInMinutes === '' ? 10 : Math.max(1, Number(expiresInMinutes));
      const expiresInSeconds = mins * 60;
      const url = await videoRecorderService.getVideoSignedUrl(video.storage_path, expiresInSeconds);
      if (url) {
        setSignedUrl(url);
        toast.success(`Temporary link valid for ${mins} minute${mins === 1 ? '' : 's'}`);
      } else {
        throw new Error('Could not create signed link');
      }
    } catch (error) {
      console.error('Error generating signed link:', error);
      toast.error('Failed to create temporary link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleDownload = async () => {
    if (!video) return;
    try {
      // Prefer signed URL for security; request with download disposition
      const filename = `${video.title || 'video'}.mp4`;
      const url = await videoRecorderService.getVideoSignedUrl(video.storage_path, 60 * 30, filename); // 30 min
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // Fallback: try public URL if available
        const pub = await videoRecorderService.getVideoPublicUrl(video.storage_path);
        if (pub) {
          const a = document.createElement('a');
          a.href = pub + (pub.includes('?') ? '&' : '?') + 'download=1';
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } else {
          toast.error('No downloadable URL available');
        }
      }
    } catch (e) {
      console.error('Download error:', e);
      toast.error('Failed to start download');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }
  
  if (!video) {
    return (
      <div className="p-4 bg-red-50 border border-red-300 rounded-md">
        <p className="text-red-700">Video not found or you don't have permission to access it.</p>
        {onClose && (
          <button 
            onClick={onClose}
            className="mt-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md shadow"
          >
            Close
          </button>
        )}
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Share "{video.title}"</h2>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      {/* Public Access Toggle */
      }
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-800">Public Access</h3>
            <p className="text-sm text-gray-600">When enabled, anyone with the link can view this video</p>
          </div>
          <button
            type="button"
            onClick={handleTogglePublic}
            aria-pressed={video.is_public}
            aria-busy={isTogglingPublic}
            disabled={isTogglingPublic}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none ${
              video.is_public ? 'bg-blue-500' : 'bg-gray-300'
            } ${isTogglingPublic ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                video.is_public ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        
        {publicLink && (
          <div className="mt-3 flex items-center space-x-2">
            <input 
              type="text" 
              value={publicLink} 
              readOnly 
              className="flex-1 p-2 text-sm border border-gray-300 rounded-md bg-gray-100"
            />
            <button 
              onClick={() => handleCopyLink(publicLink)}
              className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200"
              aria-label="Copy link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
            </button>
          </div>
        )}

        {/* Temporary direct file link and Download */}
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label htmlFor="expires-min" className="block text-sm font-medium text-gray-700">Temporary link expires in (minutes)</label>
              <input
                id="expires-min"
                type="number"
                value={expiresInMinutes}
                onChange={(e) => {
                  const val = e.target.value === '' ? '' : parseInt(e.target.value);
                  setExpiresInMinutes(val);
                  // Clear any previously generated link if params change
                  setSignedUrl(null);
                }}
                placeholder="10"
                min={1}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <button
              onClick={handleGenerateSignedLink}
              disabled={isGeneratingLink || (expiresInMinutes !== '' && (isNaN(Number(expiresInMinutes)) || Number(expiresInMinutes) < 1))}
              className={`px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 ${
                isGeneratingLink || (expiresInMinutes !== '' && (isNaN(Number(expiresInMinutes)) || Number(expiresInMinutes) < 1))
                  ? 'opacity-60 cursor-not-allowed'
                  : ''
              }`}
            >
              {isGeneratingLink ? 'Generating…' : 'Create Temporary Link'}
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
            >
              Download
            </button>
          </div>
          {signedUrl && (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={signedUrl}
                readOnly
                className="flex-1 p-2 text-sm border border-gray-300 rounded-md bg-gray-100"
              />
              <button
                onClick={() => handleCopyLink(signedUrl)}
                className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200"
              >
                Copy
              </button>
              <a
                href={signedUrl}
                target="_blank"
                rel="noreferrer"
                className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Open
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Share with specific people */}
      <div>
        <h3 className="font-medium text-gray-800 mb-2">Share with specific people</h3>
        <form onSubmit={handleCreateShare} className="space-y-3">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
            <input 
              type="email" 
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="access-type" className="block text-sm font-medium text-gray-700">Access type</label>
              <select 
                id="access-type"
                value={accessType}
                onChange={(e) => setAccessType(e.target.value as 'view' | 'edit')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="view">View only</option>
                <option value="edit">Can edit</option>
              </select>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="expiry" className="block text-sm font-medium text-gray-700">Expires after (days)</label>
              <input 
                type="number" 
                id="expiry"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value === '' ? '' : parseInt(e.target.value))}
                placeholder="Never expires"
                min="1"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          
          <div className="pt-2">
            <button 
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow flex items-center space-x-2 ${
                isSubmitting || !email.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sharing...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                  </svg>
                  <span>Share</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* List of existing shares */}
      {shares.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-800 mb-2">Shared with</h3>
          <div className="bg-gray-50 rounded-md divide-y divide-gray-200">
            {shares.map(share => (
              <div key={share.id} className="flex items-center justify-between py-3 px-4">
                <div>
                  <p className="font-medium text-gray-800">{share.shared_with_email}</p>
                  <div className="flex items-center text-sm text-gray-500 space-x-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      share.access_type === 'edit' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {share.access_type === 'edit' ? 'Can edit' : 'View only'}
                    </span>
                    {share.expires_at && (
                      <span>Expires {new Date(share.expires_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => handleRemoveShare(share.id)}
                  className="text-gray-400 hover:text-red-500"
                  aria-label="Remove share"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoShareComponent;
